import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GridModule } from '@progress/kendo-angular-grid';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { HttpClientModule } from '@angular/common/http';
import { CgpModule } from '../../../../../src/app/shared/cgp.module';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { CardGridModule } from '../../../../../src/app/shared/card-grid/card-grid.module';
import { IntlModule } from '@progress/kendo-angular-intl';
import { UploadModule } from '@progress/kendo-angular-upload';
import { WorkOrderGuard } from './work-order.guard';
import { AuthGuard } from '../../../auth.guard';

/** COMPONENTS */
import { WorkOrdersComponent } from './work-orders.component';
import { WorkOrderHistoryComponent } from './work-order-history/work-order-history.component';
import { WorkOrderCopyDialogComponent } from './dialogs/work-order-copy-dialog/work-order-copy-dialog.component';
import { WorkOrderProgramDialogComponent } from './dialogs/work-order-program-dialog/work-order-program-dialog.component';
import { WorkOrderSearchDialogComponent } from './dialogs/work-order-search-dialog/work-order-search-dialog.component';
import { WorkOrderSearchFormComponent } from './forms/work-order-search-form/work-order-search-form.component';
import { WorkOrderSearchKeywordFormComponent } from './forms/work-order-search-keyword-form/work-order-search-keyword-form.component';
import { WorkOrderDocumentDialogComponent } from './dialogs/work-order-document-dialog/work-order-document-dialog.component';
import { WorkOrderFileUploadComponent } from './work-order-file-upload/work-order-file-upload.component';

import { GaWoAddComponent } from './GA/ga-wo-add/ga-wo-add.component';
import { GaWoEditComponent } from './GA/ga-wo-edit/ga-wo-edit.component';
import { GaAddMultipleComponent } from './GA/ga-add-multiple/ga-add-multiple.component';
import { GaMainFormComponent } from './GA/forms/ga-main-form/ga-main-form.component';
import { GaAddInfoFormComponent } from './GA/forms/ga-add-info-form/ga-add-info-form.component';
import { GaDeliverablesObjFormComponent } from './GA/forms/ga-deliverables-obj-form/ga-deliverables-obj-form.component';
import { GaHeaderMessageFormComponent } from './GA/forms/ga-header-message-form/ga-header-message-form.component';
import { GaCommentsFormComponent } from './GA/forms/ga-comments-form/ga-comments-form.component';
import { GaAddMultipleFormComponent } from './GA/forms/ga-add-multiple-form/ga-add-multiple-form.component';
import { GaStudyTitlesDialogComponent } from './GA/dialogs/ga-study-titles-dialog/ga-study-titles-dialog.component';
import { GaNotificationDialogComponent } from './GA/dialogs/ga-notification-dialog/ga-notification-dialog.component';
import { GaUploadFileDialogComponent } from './GA/dialogs/ga-upload-file-dialog/ga-upload-file-dialog.component';
import { GaWoSubmitReportDialogComponent } from './GA/dialogs/ga-wo-submit-report-dialog/ga-wo-submit-report-dialog.component';
import { GaWoAssignedReportFormComponent } from './GA/forms/reports/ga-wo-assigned-report-form/ga-wo-assigned-report-form.component';
import { GaWoProgressReportFormComponent } from './GA/forms/reports/ga-wo-progress-report-form/ga-wo-progress-report-form.component';
import { GaWoCompleteReportFormComponent } from './GA/forms/reports/ga-wo-complete-report-form/ga-wo-complete-report-form.component';
import { GaWoHoldReportFormComponent } from './GA/forms/reports/ga-wo-hold-report-form/ga-wo-hold-report-form.component';
import { GaWoCancelledReportFormComponent } from './GA/forms/reports/ga-wo-cancelled-report-form/ga-wo-cancelled-report-form.component';
import { GaWoReportFilesComponent } from './GA/forms/reports/ga-wo-report-files/ga-wo-report-files.component';
import { TabContentLoadOnDemandDirective } from '../work-orders/GA/forms/reports/ga-wo-report-files/lazyload.directive';

import { BodyWoAddComponent } from './Body/body-wo-add/body-wo-add.component';
import { BodyWoEditComponent } from './Body/body-wo-edit/body-wo-edit.component';
import { BodyWoAddMultipleComponent } from './Body/body-wo-add-multiple/body-wo-add-multiple.component';
import { BodyHeaderMessageFormComponent } from './Body/forms/body-header-message-form/body-header-message-form.component';
import { BodyMainFormComponent } from './Body/forms/body-main-form/body-main-form.component';
import { BodyCommentsFormComponent } from './Body/forms/body-comments-form/body-comments-form.component';
import { BodyAddMultipleFormComponent } from './Body/forms/body-add-multiple-form/body-add-multiple-form.component';
import { BodyWoSubmitReportDialogComponent } from './Body/dialogs/body-wo-submit-report-dialog/body-wo-submit-report-dialog.component';
import { BodyWoSubmitReportAssignedFormComponent } from './Body/forms/body-wo-submit-report-assigned-form/body-wo-submit-report-assigned-form.component';
import { BodyWoSubmitReportInProgressFormComponent } from './Body/forms/body-wo-submit-report-in-progress-form/body-wo-submit-report-in-progress-form.component';
import { BodyWoSubmitReportCompleteFormComponent } from './Body/forms/body-wo-submit-report-complete-form/body-wo-submit-report-complete-form.component';
import { BodyWoSubmitReportOnHoldFormComponent } from './Body/forms/body-wo-submit-report-on-hold-form/body-wo-submit-report-on-hold-form.component';
import { BodyWoSubmitReportCancelledFormComponent } from './Body/forms/body-wo-submit-report-cancelled-form/body-wo-submit-report-cancelled-form.component';
import { BodyNotificationDialogComponent } from './Body/dialogs/body-notification-dialog/body-notification-dialog.component';
import { BodyDeliverablesDialogComponent } from './Body/dialogs/body-deliverables-dialog/body-deliverables-dialog.component';

/** SERVICES */
import { WorkOrdersService } from './work-orders.service';
import { ReportsService } from './GA/forms/reports/reports.service';
import { FileUploadService } from './work-order-file-upload/file-upload.service';
import { WorkOrdersFilterService } from './work-orders-filter.service';
import {
    AuthorizationService,
    BodyWorkOrderService,
    DocumentService,
    GAWorkOrderService,
    GlobalRegionService,
    UserSettingsService,
    WorkOrderHistoryService,
    WorkOrderService,
} from '../ve.service';

import { WorkOrderDocumentDialogService } from './dialogs/work-order-document-dialog/work-order-document-document.service';
import { WorkOrdersReportsComponent } from './work-orders-reports/work-orders-reports.component';
import { WorkOrdersSearchComponent } from './work-orders-search/work-orders-search.component';
import { WoAssignFormComponent } from '../work-orders/forms/wo-assign-form/wo-assign-form.component';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatIconModule } from '@angular/material/icon';

const WORK_ORDERS_VIEW_NUMBER_PATH = `Wo-View/:work-order-number`;
const WORK_ORDERS_ADD_PATH = `Wo-Add`;
const WORK_ORDERS_EDIT_PATH = `Wo-Edit`;
const WORK_ORDERS_COPY_PATH = `Wo-Copy`;
const WORK_ORDERS_VIEW_PATH = `Wo-View`;
const WORK_ORDERS_ADD_MULTIPLE_PATH = `Wo-Add-Multiple`;

/** ROUTES */
export const workOrdersRoutes = {
    path: 'Work-Orders',
    component: WorkOrdersComponent,
    canActivate: [AuthGuard],
    data: {
        expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
    },
    children:
        [
            {
                path: `Ga-${WORK_ORDERS_VIEW_NUMBER_PATH}`,
                component: WorkOrdersComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Ga-${WORK_ORDERS_VIEW_NUMBER_PATH}`,
                component: WorkOrdersComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Ga-${WORK_ORDERS_ADD_PATH}`,
                component: GaWoAddComponent,
                canActivate: [AuthGuard, WorkOrderGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Ga-${WORK_ORDERS_EDIT_PATH}`,
                component: GaWoEditComponent,
                canActivate: [WorkOrderGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Ga-${WORK_ORDERS_COPY_PATH}`,
                component: GaWoEditComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Ga-${WORK_ORDERS_VIEW_PATH}`,
                component: GaWoEditComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Ga-${WORK_ORDERS_ADD_MULTIPLE_PATH}`,
                component: GaAddMultipleComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Lead']
                },
            },
            {
                path: `Body-${WORK_ORDERS_VIEW_NUMBER_PATH}`,
                component: WorkOrdersComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Body-${WORK_ORDERS_ADD_PATH}`,
                component: BodyWoAddComponent,
                canActivate: [AuthGuard, WorkOrderGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Body-${WORK_ORDERS_EDIT_PATH}`,
                component: BodyWoEditComponent,
                canActivate: [WorkOrderGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Body-${WORK_ORDERS_COPY_PATH}`,
                component: BodyWoEditComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Body-${WORK_ORDERS_VIEW_PATH}`,
                component: BodyWoEditComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
            },
            {
                path: `Body-${WORK_ORDERS_ADD_MULTIPLE_PATH}`,
                component: BodyWoAddMultipleComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Lead']
                },
            },
            {
                path: 'Reports',
                component: WorkOrdersReportsComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
                children: [
                    {
                        path: `Ga-${WORK_ORDERS_VIEW_NUMBER_PATH}`,
                        component: WorkOrdersComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_ADD_PATH}`,
                        component: GaWoAddComponent,
                        canActivate: [AuthGuard, WorkOrderGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_EDIT_PATH}`,
                        component: GaWoEditComponent,
                        canActivate: [WorkOrderGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_COPY_PATH}`,
                        component: GaWoEditComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_VIEW_PATH}`,
                        component: GaWoEditComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_ADD_MULTIPLE_PATH}`,
                        component: GaAddMultipleComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Lead']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_VIEW_NUMBER_PATH}`,
                        component: WorkOrdersComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_ADD_PATH}`,
                        component: BodyWoAddComponent,
                        canActivate: [AuthGuard, WorkOrderGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_EDIT_PATH}`,
                        component: BodyWoEditComponent,
                        canActivate: [WorkOrderGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_COPY_PATH}`,
                        component: BodyWoEditComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_VIEW_PATH}`,
                        component: BodyWoEditComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_ADD_MULTIPLE_PATH}`,
                        component: BodyWoAddMultipleComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Lead']
                        },
                    },
                ]
            },
            {
                path: 'Search',
                component: WorkOrdersSearchComponent,
                canActivate: [AuthGuard],
                data: {
                    expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                },
                children: [
                    {
                        path: `Ga-${WORK_ORDERS_VIEW_NUMBER_PATH}`,
                        component: WorkOrdersComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_ADD_PATH}`,
                        component: GaWoAddComponent,
                        canActivate: [AuthGuard, WorkOrderGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_EDIT_PATH}`,
                        component: GaWoEditComponent,
                        canActivate: [WorkOrderGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_COPY_PATH}`,
                        component: GaWoEditComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_VIEW_PATH}`,
                        component: GaWoEditComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Ga-${WORK_ORDERS_ADD_MULTIPLE_PATH}`,
                        component: GaAddMultipleComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Lead']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_VIEW_NUMBER_PATH}`,
                        component: WorkOrdersComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_ADD_PATH}`,
                        component: BodyWoAddComponent,
                        canActivate: [AuthGuard, WorkOrderGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_EDIT_PATH}`,
                        component: BodyWoEditComponent,
                        canActivate: [WorkOrderGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_COPY_PATH}`,
                        component: BodyWoEditComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_VIEW_PATH}`,
                        component: BodyWoEditComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Manager', 'Lead', 'Engineer', 'User']
                        },
                    },
                    {
                        path: `Body-${WORK_ORDERS_ADD_MULTIPLE_PATH}`,
                        component: BodyWoAddMultipleComponent,
                        canActivate: [AuthGuard],
                        data: {
                            expectedRoles: ['Administrator', 'Lead']
                        },
                    },
                ]
            },
        ],
};

/** MODULES */
@NgModule({
    imports: [
        CardGridModule,
        CgpModule,
        CommonModule,
        DropDownsModule,
        FormsModule,
        GridModule,
        HttpClientModule,
        IntlModule,
        LayoutModule,
        MatButtonModule,
        MatCardModule,
        MatDialogModule,
        MatIconModule,
        ReactiveFormsModule,
        RouterModule,
        UploadModule
    ],
    declarations: [
        BodyAddMultipleFormComponent,
        BodyCommentsFormComponent,
        BodyDeliverablesDialogComponent,
        BodyHeaderMessageFormComponent,
        BodyMainFormComponent,
        BodyNotificationDialogComponent,
        BodyWoAddComponent,
        BodyWoAddMultipleComponent,
        BodyWoEditComponent,
        GaAddInfoFormComponent,
        GaAddMultipleComponent,
        GaAddMultipleFormComponent,
        GaCommentsFormComponent,
        GaDeliverablesObjFormComponent,
        GaHeaderMessageFormComponent,
        GaMainFormComponent,
        GaNotificationDialogComponent,
        GaStudyTitlesDialogComponent,
        GaWoAddComponent,
        GaWoEditComponent,
        WorkOrderCopyDialogComponent,
        WorkOrderHistoryComponent,
        WorkOrdersComponent,
        WorkOrderProgramDialogComponent,
        WorkOrderSearchDialogComponent,
        WorkOrderSearchFormComponent,
        WorkOrderSearchKeywordFormComponent,
        BodyWoSubmitReportDialogComponent,
        BodyWoSubmitReportAssignedFormComponent,
        BodyWoSubmitReportInProgressFormComponent,
        BodyWoSubmitReportCompleteFormComponent,
        BodyWoSubmitReportOnHoldFormComponent,
        BodyWoSubmitReportCancelledFormComponent,
        GaWoSubmitReportDialogComponent,
        GaWoAssignedReportFormComponent,
        GaWoProgressReportFormComponent,
        GaWoCompleteReportFormComponent,
        GaWoHoldReportFormComponent,
        GaWoCancelledReportFormComponent,
        GaWoReportFilesComponent,
        TabContentLoadOnDemandDirective,
        WorkOrderDocumentDialogComponent,
        WorkOrderFileUploadComponent,
        GaUploadFileDialogComponent,
        WorkOrdersReportsComponent,
        WorkOrdersSearchComponent,
        WoAssignFormComponent,
    ],
    providers: [
        AuthorizationService,
        BodyWorkOrderService,
        DocumentService,
        GAWorkOrderService,
        GlobalRegionService,
        UserSettingsService,
        WorkOrderGuard,
        WorkOrderHistoryService,
        WorkOrderService,
        WorkOrdersFilterService,
        WorkOrdersService,
        ReportsService,
        WorkOrderDocumentDialogService,
        FileUploadService
    ]
})

export class WorkOrdersModule { }
